import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToggleGroup, ConfirmDialog } from "components";
import { selectQuestionById } from "features/question/questionSlice";
import { selectGameId } from "features/gamestate/gameStateSlice";
import { selectCurrentUser } from "features/auth/authSlice";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";
import Provider from "provider";
import logging from "utils/logging";
import {
  clearPanelSelection,
  panelSaving,
  panelSaved,
  panelSaveError,
} from "./editorSlice";
import "./roundEditor.css";

const log = logging.getLogger("ClueEditForm");

const PROMPT_OPTIONS = [
  { value: "", label: "None" },
  { value: "What is", label: '"What is…"' },
  { value: "Who is", label: '"Who is…"' },
];

const ClueEditForm = ({ questionId, round, isFinal = false }) => {
  const dispatch = useDispatch();
  const question = useSelector((state) => selectQuestionById(state, questionId));
  const gameId = useSelector(selectGameId);
  const currentUser = useSelector(selectCurrentUser);

  const [clue, setClue] = useState("");
  const [answersRaw, setAnswersRaw] = useState("");
  const [prompt, setPrompt] = useState("");
  const [dailyDouble, setDailyDouble] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadFromQuestion = (q) => {
    setClue(q?.clue || "");
    setAnswersRaw(Array.isArray(q?.answers) ? q.answers.join(", ") : "");
    setPrompt(q?.prompt || "");
    setDailyDouble(!!q?.dailyDouble);
  };

  useEffect(() => {
    if (question) loadFromQuestion(question);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  if (!question) return null;

  const editor =
    currentUser?.displayName || currentUser?.name || currentUser?.uid || "unknown";
  const catLabel = question.category || "";
  const valueLabel = isFinal ? "Wager" : `$${question.value || ""}`;
  const headerText = [catLabel, valueLabel].filter(Boolean).join(" · ");

  const parsedAnswers = () =>
    answersRaw
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean);

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);
    dispatch(panelSaving());
    try {
      const answers = parsedAnswers();
      await Provider.updateQuestion(gameId, questionId, {
        clue,
        answers,
        prompt,
        dailyDouble,
        clueType: "text",
        updatedBy: editor,
        source: "manual",
      });
      dispatch(panelSaved());
      Analytics.track(EVENTS.CLUE_SAVED, {
        game_id: gameId,
        round,
        question_id: questionId,
        daily_double: dailyDouble,
        clue_length: clue.length,
        answer_count: answers.length,
      });
    } catch (err) {
      log.error("Failed to save clue", { gameId, questionId, round, err });
      dispatch(panelSaveError());
    } finally {
      setSubmitting(false);
    }
  };

  // Daily Double is a one-click control: flip it and persist immediately,
  // without requiring a separate Save. Reverts on failure.
  const handleToggleDailyDouble = async () => {
    if (isFinal || submitting) return;
    const next = !dailyDouble;
    setDailyDouble(next);
    setSubmitting(true);
    dispatch(panelSaving());
    try {
      await Provider.updateQuestion(gameId, questionId, {
        dailyDouble: next,
        clueType: "text",
        updatedBy: editor,
        source: "manual",
      });
      dispatch(panelSaved());
      Analytics.track(EVENTS.CLUE_SAVED, {
        game_id: gameId,
        round,
        question_id: questionId,
        daily_double: next,
        clue_length: clue.length,
        answer_count: parsedAnswers().length,
      });
    } catch (err) {
      log.error("Failed to toggle Daily Double", {
        gameId,
        questionId,
        round,
        err,
      });
      setDailyDouble(!next);
      dispatch(panelSaveError());
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    dispatch(clearPanelSelection());
    Analytics.track(EVENTS.PANEL_CANCELLED, {
      game_id: gameId,
      round,
      panel_mode: "clue",
    });
  };

  // Clear erases this cell's content (clue + answers + prompt + DD) and persists
  // it, leaving the cell empty. Panel stays open so a new clue can be written.
  const handleConfirmClear = async () => {
    setConfirmClearOpen(false);
    if (submitting) return;
    setSubmitting(true);
    dispatch(panelSaving());
    try {
      await Provider.updateQuestion(gameId, questionId, {
        clue: "",
        answers: [],
        prompt: "",
        dailyDouble: false,
        clueType: "text",
        updatedBy: editor,
        source: "manual",
      });
      setClue("");
      setAnswersRaw("");
      setPrompt("");
      setDailyDouble(false);
      dispatch(panelSaved());
      Analytics.track(EVENTS.CLUE_SAVED, {
        game_id: gameId,
        round,
        question_id: questionId,
        daily_double: false,
        clue_length: 0,
        answer_count: 0,
      });
    } catch (err) {
      log.error("Failed to clear clue", { gameId, questionId, round, err });
      dispatch(panelSaveError());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="re-panel-header">
        <span className="re-panel-header-title">{headerText}</span>
        <button
          type="button"
          className="re-panel-close"
          aria-label="Close editor"
          onClick={handleCancel}
          disabled={submitting}
        >
          ×
        </button>
      </div>

      <div className="re-panel-body">
        {/* Clue field */}
        <div className="re-field">
          <label className="re-label">Clue</label>
          <div className="re-media-slot" />
          <textarea
            className="re-textarea"
            rows={3}
            placeholder="Write the clue here…"
            value={clue}
            onChange={(e) => setClue(e.target.value)}
          />
        </div>

        {/* Answers field */}
        <div className="re-field">
          <label className="re-label">Acceptable answers</label>
          <textarea
            className="re-textarea"
            rows={2}
            placeholder="e.g. soba, soba noodles"
            value={answersRaw}
            onChange={(e) => setAnswersRaw(e.target.value)}
          />
          <span className="re-helper">Comma-separated — e.g. soba, soba noodles</span>
        </div>

        {/* Prompt picker */}
        <div className="re-field">
          <label className="re-label">
            Prompt <span className="re-label-optional">(optional)</span>
          </label>
          <ToggleGroup
            options={PROMPT_OPTIONS}
            value={prompt}
            onChange={setPrompt}
            size="sm"
          />
          <span className="re-helper">Shown before the clue on the player screen</span>
        </div>

        {/* Daily Double — one-click toggle, saves immediately. Hidden for Final. */}
        {!isFinal && (
          <div className="re-dd-toggle">
            <div className="re-dd-toggle-label">
              <div className="re-dd-toggle-title">Daily Double</div>
              <div className="re-dd-toggle-desc">Wager up to current max · plays alone</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={dailyDouble}
              aria-label="Daily Double"
              className={`re-switch ${dailyDouble ? "is-on" : ""}`}
              onClick={handleToggleDailyDouble}
              disabled={submitting}
            >
              <span className="re-switch-knob" />
            </button>
          </div>
        )}
      </div>

      {/* Metadata footer */}
      {(question.updatedAt || question.updatedBy) && (
        <div className="re-panel-footer">
          {question.updatedBy && <span>Last edited by {question.updatedBy}</span>}
          {question.source && <span>Source: {question.source}</span>}
        </div>
      )}

      <div className="re-panel-actions">
        <button
          type="button"
          onClick={() => setConfirmClearOpen(true)}
          disabled={submitting}
          style={{
            color: "var(--color-error, #ef4444)",
            background: "none",
            border: "none",
            cursor: submitting ? "not-allowed" : "pointer",
            opacity: submitting ? 0.5 : 1,
            fontSize: "inherit",
          }}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={submitting}
          style={{
            background: "none",
            border: "none",
            cursor: submitting ? "not-allowed" : "pointer",
            opacity: submitting ? 0.5 : 1,
            fontSize: "inherit",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={submitting}
          style={{
            background: "var(--color-teal, #2ec4b6)",
            color: "#fff",
            border: "none",
            borderRadius: "var(--radius-md, 8px)",
            padding: "8px 18px",
            cursor: submitting ? "not-allowed" : "pointer",
            opacity: submitting ? 0.6 : 1,
            fontWeight: 600,
            fontSize: "inherit",
          }}
        >
          {submitting ? "Saving…" : "Save clue"}
        </button>
      </div>

      <ConfirmDialog
        open={confirmClearOpen}
        title="Clear this clue?"
        message="This erases the clue text and acceptable answers for this cell, leaving it empty. You can write a new clue afterward."
        confirmLabel="Clear clue"
        cancelLabel="Keep it"
        destructive
        onConfirm={handleConfirmClear}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </>
  );
};

export default ClueEditForm;
